import React from 'react';
import { showDialog } from '../dialog';
import Logger from '../../services/Logger';
import l10n from '../../shared/strings';
import { addSavedSearch, createEmptyGroup as createEmptyGroupAction } from '../../store/savedSearches/actions';
import { conflictForNewSavedSearch } from '../../store/helpers/savedSearches/pathConflict';
import store from '../../store';
import SaveSearchDialog, { SAVE_SEARCH_DIALOG_NAME_INPUT_TEST_ID } from './SaveSearchDialog';

const LAST_USED_SCOPE_KEY = 'picsio.savedSearches.lastUsedScope';

function readLastUsedScope() {
  try {
    const v = window.localStorage.getItem(LAST_USED_SCOPE_KEY);
    return v === 'team' || v === 'personal' ? v : null;
  } catch (e) {
    return null;
  }
}

function writeLastUsedScope(scope) {
  try {
    window.localStorage.setItem(LAST_USED_SCOPE_KEY, scope);
  } catch (e) {
    /* localStorage may be unavailable (private mode, ssr) — silently ignore */
  }
}

/**
 * Show the new "Save this search" modal (Nested Saved Searches v2).
 *
 * Mirrors the previous showDialog-based flow but adds a group picker and an
 * inline "+ New group" action. The host dialog still owns name + Ok/Cancel;
 * this helper owns the rest of the form via the SaveSearchDialog children.
 *
 * Telemetry mirrors the legacy event names where possible so existing
 * dashboards keep working.
 *
 * @param {object} args - Arguments object.
 * @param {object} args.user - Redux user slice.
 * @param {object} args.searchData - Value to put into newItem.data.
 * @param {'products'|'assets'|undefined} args.entity - Saved search entity (top-level field).
 * @param {string|undefined} args.searchValue - Plain-text search value (logged for analytics).
 * @param {object|undefined} args.confirmedFilters - Current filters (logged for analytics).
 * @param {string|undefined} args.defaultName - Pre-fill for the name input.
 */
export default function showSaveSearchDialog({
  user,
  searchData,
  entity,
  searchValue,
  confirmedFilters,
  defaultName = '',
}) {
  const { dispatch } = store;
  const canShare = Boolean(user?.role?.permissions?.manageTeamSavedSearches);

  // Default scope: prefer Team Saved Searches whenever the user can share —
  // team-wide visibility is the more common intent, and personal scope is a
  // niche/private fallback. Once the user explicitly picks personal we
  // remember that choice for next time. Users without share permission fall
  // back to personal automatically.
  const lastUsedScope = readLastUsedScope();
  const defaultShared = canShare && lastUsedScope !== 'personal';

  // Always start with the scope's root selected — a neutral default that
  // doesn't surprise the user with a pre-picked nested group. They can drill
  // into a specific group via the tree if they want.
  const defaultPath = '';

  // Closure box updated by SaveSearchDialog.onSelectionChange. The host
  // dialog reads from it inside onOk to know the current group/scope.
  const selection = {
    path: defaultPath,
    shared: defaultShared,
    pendingNewGroup: false,
  };

  const onSelectionChange = (next) => {
    selection.path = next.path;
    selection.shared = next.shared;
    selection.pendingNewGroup = next.pendingNewGroup;
  };

  const validateSearchName = (v) => {
    if (v === '') return true;
    const trimmed = (v || '').trim();
    if (!trimmed) return false;
    const state = store.getState();
    const { all } = state.savedSearches;
    const userId = state.user?._id;
    if (conflictForNewSavedSearch(all, userId, {
      parentPath: selection.path || '',
      name: trimmed,
      isShared: selection.shared,
    })) {
      return l10n.SAVEDSEARCHESTREE.textDuplicateSavedSearchInLocation;
    }
    return true;
  };

  const onCreateGroup = async ({ parentPath, name, isShared }) => {
    Logger.log('User', 'SaveSearchModalNewGroupCreated', {
      parentPath: parentPath || null,
      isShared,
    });
    const result = await dispatch(createEmptyGroupAction({ parentPath, name, isShared })).unwrap();
    return result.placeholder.path;
  };

  showDialog({
    title: l10n.SEARCH.dialogTitleCreateSearch,
    // The host dialog renders `input` after `children` by default, but UX-wise
    // the Name field is the primary action and should sit at the top. We use
    // a custom class on the dialog wrapper to flip the order of these two
    // slots via flex `order` (see SaveSearchDialog/styles.module.scss).
    className: 'saveSearchHostDialog',
    input: {
      label: l10n.SEARCH.dialogCreateSearchLabel,
      dataTestId: SAVE_SEARCH_DIALOG_NAME_INPUT_TEST_ID,
      value: defaultName,
      autoFocus: !defaultName,
      placeholder: l10n.SEARCH.dialogCreateSearchPlaceholder,
      // Empty string = "not entered yet", not an error — keeps the input
      // neutral on open. Whitespace-only input still flags as invalid so the
      // user gets a hint why Ok stays disabled. The actual submit guard is
      // `disableOk` below.
      validate: validateSearchName,
    },
    disableOk: (s) => {
      const trimmed = (s.input || '').trim();
      if (!trimmed) return true;
      if (selection.pendingNewGroup) return true;
      return Boolean(s.errors?.input);
    },
    children: (
      <SaveSearchDialog
        defaultPath={defaultPath}
        defaultShared={defaultShared}
        canShare={canShare}
        onSelectionChange={onSelectionChange}
        onCreateGroup={onCreateGroup}
      />
    ),
    onOk: (data) => {
      const name = (data.input || '').trim();
      if (!name) return; // disableOk should prevent this, defensive
      if (selection.pendingNewGroup) return;

      Logger.log('User', 'SaveSearchModalSubmitted', {
        active_filters_list: confirmedFilters,
        search_name: name,
        shared_with_team: selection.shared,
        broad_search_value: searchValue,
        path: selection.path || null,
      });
      writeLastUsedScope(selection.shared ? 'team' : 'personal');

      dispatch(addSavedSearch({
        name,
        data: searchData,
        entity,
        shared: selection.shared,
        path: selection.path || undefined,
      }));
    },
    onCancel: () => {
      Logger.log('User', 'SaveSearchModalCancelled');
    },
  });

  Logger.log('User', 'SaveSearchModalShown', {
    initialPath: defaultPath || null,
    initialShared: defaultShared,
  });
}
